import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import useLocalStorage from '../hooks/useLocalStorage';
import { User, XCircle, Maximize2, Minimize2, Plus, Minus, Clock, RotateCcw, Play, Pause } from 'lucide-react';
import { reorderAnswers } from '../utils/reorderAnswers';
import rightSound from '../assets/right.mp3';
import wrongSound from '../assets/wrong.mp3';

const GamePage = () => {
  const [teams, setTeams] = useLocalStorage('feud-teams', []);
  const [questions] = useLocalStorage('feud-questions', []);
  const [currentQuestionIdx, setCurrentQuestionIdx] = useState(0);
  const [revealedAnswers, setRevealedAnswers] = useState([]);
  const [activeTeamIdx, setActiveTeamIdx] = useState(null);
  const [showX, setShowX] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const [timerDuration] = useLocalStorage('feud-timer-duration', 30);
  const [backgroundImageUrl] = useLocalStorage('feud-background-image-url', '');
  const [theme] = useLocalStorage('feud-theme', 'default');
  const [timeLeft, setTimeLeft] = useState(timerDuration);
  const [isTimerRunning, setIsTimerRunning] = useState(false);

  useEffect(() => {
    document.title = "Populate Vote - Game";
  }, []);

  const containerRef = React.useRef(null);
  const currentQuestion = questions[currentQuestionIdx];

  useEffect(() => {
    setRevealedAnswers([]);
    setTimeLeft(timerDuration);
    setIsTimerRunning(false);
  }, [currentQuestionIdx, timerDuration]);

  useEffect(() => {
    let interval = null;
    if (isTimerRunning && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft((prev) => prev - 1);
      }, 1000);
    } else if (timeLeft === 0) {
      setIsTimerRunning(false);
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [isTimerRunning, timeLeft]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().catch(err => {
        alert(`Error attempting to enable full-screen mode: ${err.message} (${err.name})`);
      });
    } else {
      document.exitFullscreen();
    }
  };

  const playSound = (type) => {
    const sounds = {
      reveal: rightSound,
      wrong: wrongSound
    };
    
    const soundUrl = sounds[type];
    if (!soundUrl) return;

    const audio = new Audio(soundUrl);
    
    // Explicitly set volume and try to play
    audio.volume = 1.0;
    
    audio.play().catch(e => {
      console.warn(`[GamePage] Failed to play ${type} sound:`, e.message);
      // Some browsers require a user gesture, which we have here, 
      // but if the URL is invalid or blocked by CORS, it will fail.
    });
  };

  const revealAnswer = (idx) => {
    if (revealedAnswers.includes(idx)) return;
    
    setRevealedAnswers([...revealedAnswers, idx]);
    // Wait for the card flip animation (500ms) to finish before playing the sound
    setTimeout(() => {
      playSound('reveal');
    }, 500);
  };

  const updateTeamScore = (teamIdx, delta) => {
    const newTeams = [...teams];
    newTeams[teamIdx].score = Math.max(0, newTeams[teamIdx].score + delta);
    setTeams(newTeams);
  };

  const triggerX = () => {
    playSound('wrong');
    setShowX(true);
    setTimeout(() => setShowX(false), 1500);
  };

  const resetTimer = () => {
    setTimeLeft(timerDuration);
    setIsTimerRunning(false);
  };

  const toggleTimer = () => {
    if (timeLeft > 0) {
      setIsTimerRunning(!isTimerRunning);
    }
  };

  const getThemeClasses = () => {
    const themes = {
      'default': {
        teamInactive: 'border-transparent bg-blue-900/80 shadow-md backdrop-blur-sm',
        teamActive: 'border-yellow-400 bg-blue-800 scale-105 shadow-[0_0_20px_rgba(250,204,21,0.4)]',
        questionHeader: 'bg-gradient-to-r from-blue-900 via-blue-800 to-blue-900 border-b-2 border-blue-400 text-shadow',
        answerFront: 'from-blue-500 to-blue-700 border-blue-400 shadow-[inset_0_2px_10px_rgba(255,255,255,0.2)]',
        answerFrontCircle: 'bg-blue-900 border-blue-300 shadow-inner',
        answerBack: 'from-yellow-400 to-yellow-600 text-blue-950 border-yellow-300 shadow-[inset_0_2px_10px_rgba(255,255,255,0.4)]',
        answerBackDivider: 'border-blue-900'
      },
      'modern-dark': {
        teamInactive: 'border-transparent bg-gray-800/80 shadow-md backdrop-blur-sm',
        teamActive: 'border-cyan-400 bg-gray-700 scale-105 shadow-[0_0_20px_rgba(34,211,238,0.4)]',
        questionHeader: 'bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 border-b-2 border-cyan-500',
        answerFront: 'from-gray-600 to-gray-800 border-gray-500 shadow-[inset_0_2px_10px_rgba(255,255,255,0.1)]',
        answerFrontCircle: 'bg-gray-900 border-gray-400',
        answerBack: 'from-cyan-400 to-cyan-600 text-gray-950 border-cyan-300 shadow-[inset_0_2px_10px_rgba(255,255,255,0.3)]',
        answerBackDivider: 'border-gray-900'
      },
      'classic-gold': {
        teamInactive: 'border-transparent bg-amber-900/80 shadow-md backdrop-blur-sm',
        teamActive: 'border-yellow-300 bg-amber-800 scale-105 shadow-[0_0_20px_rgba(253,224,71,0.4)]',
        questionHeader: 'bg-gradient-to-r from-amber-950 via-amber-900 to-amber-950 border-b-2 border-yellow-500',
        answerFront: 'from-amber-600 to-amber-800 border-amber-500 shadow-[inset_0_2px_10px_rgba(255,255,255,0.2)]',
        answerFrontCircle: 'bg-amber-950 border-amber-400',
        answerBack: 'from-yellow-300 to-yellow-500 text-amber-950 border-yellow-200 shadow-[inset_0_2px_10px_rgba(255,255,255,0.5)]',
        answerBackDivider: 'border-amber-950'
      },
      'emerald': {
        teamInactive: 'border-transparent bg-emerald-900/80 shadow-md backdrop-blur-sm',
        teamActive: 'border-green-300 bg-emerald-800 scale-105 shadow-[0_0_20px_rgba(134,239,172,0.4)]',
        questionHeader: 'bg-gradient-to-r from-emerald-950 via-emerald-900 to-emerald-950 border-b-2 border-green-400',
        answerFront: 'from-emerald-600 to-emerald-800 border-emerald-500 shadow-[inset_0_2px_10px_rgba(255,255,255,0.2)]',
        answerFrontCircle: 'bg-emerald-950 border-emerald-400',
        answerBack: 'from-green-300 to-green-500 text-emerald-950 border-green-200 shadow-[inset_0_2px_10px_rgba(255,255,255,0.4)]',
        answerBackDivider: 'border-emerald-950'
      },
      'royal-purple': {
        teamInactive: 'border-transparent bg-purple-900/80 shadow-md backdrop-blur-sm',
        teamActive: 'border-fuchsia-300 bg-purple-800 scale-105 shadow-[0_0_20px_rgba(240,171,252,0.4)]',
        questionHeader: 'bg-gradient-to-r from-purple-950 via-purple-900 to-purple-950 border-b-2 border-fuchsia-400',
        answerFront: 'from-purple-600 to-purple-800 border-purple-500 shadow-[inset_0_2px_10px_rgba(255,255,255,0.2)]',
        answerFrontCircle: 'bg-purple-950 border-purple-400',
        answerBack: 'from-fuchsia-400 to-fuchsia-600 text-purple-950 border-fuchsia-300 shadow-[inset_0_2px_10px_rgba(255,255,255,0.4)]',
        answerBackDivider: 'border-purple-950'
      },
      'sunset-orange': {
        teamInactive: 'border-transparent bg-orange-900/80 shadow-md backdrop-blur-sm',
        teamActive: 'border-amber-300 bg-orange-800 scale-105 shadow-[0_0_20px_rgba(252,211,77,0.4)]',
        questionHeader: 'bg-gradient-to-r from-orange-950 via-orange-900 to-orange-950 border-b-2 border-amber-400',
        answerFront: 'from-orange-500 to-orange-700 border-orange-400 shadow-[inset_0_2px_10px_rgba(255,255,255,0.2)]',
        answerFrontCircle: 'bg-orange-950 border-orange-300',
        answerBack: 'from-amber-300 to-amber-500 text-orange-950 border-amber-200 shadow-[inset_0_2px_10px_rgba(255,255,255,0.4)]',
        answerBackDivider: 'border-orange-950'
      },
      'midnight-rose': {
        teamInactive: 'border-transparent bg-rose-900/80 shadow-md backdrop-blur-sm',
        teamActive: 'border-pink-300 bg-rose-800 scale-105 shadow-[0_0_20px_rgba(249,168,212,0.4)]',
        questionHeader: 'bg-gradient-to-r from-rose-950 via-rose-900 to-rose-950 border-b-2 border-pink-400',
        answerFront: 'from-rose-600 to-rose-800 border-rose-500 shadow-[inset_0_2px_10px_rgba(255,255,255,0.2)]',
        answerFrontCircle: 'bg-rose-950 border-rose-400',
        answerBack: 'from-pink-400 to-pink-600 text-rose-950 border-pink-300 shadow-[inset_0_2px_10px_rgba(255,255,255,0.4)]',
        answerBackDivider: 'border-rose-950'
      },
      'ocean-breeze': {
        teamInactive: 'border-transparent bg-teal-900/80 shadow-md backdrop-blur-sm',
        teamActive: 'border-cyan-300 bg-teal-800 scale-105 shadow-[0_0_20px_rgba(103,232,249,0.4)]',
        questionHeader: 'bg-gradient-to-r from-teal-950 via-teal-900 to-teal-950 border-b-2 border-cyan-400',
        answerFront: 'from-teal-600 to-teal-800 border-teal-500 shadow-[inset_0_2px_10px_rgba(255,255,255,0.2)]',
        answerFrontCircle: 'bg-teal-950 border-teal-400',
        answerBack: 'from-cyan-300 to-cyan-500 text-teal-950 border-cyan-200 shadow-[inset_0_2px_10px_rgba(255,255,255,0.4)]',
        answerBackDivider: 'border-teal-950'
      },
      'slate-grey': {
        teamInactive: 'border-transparent bg-slate-800/80 shadow-md backdrop-blur-sm',
        teamActive: 'border-slate-300 bg-slate-700 scale-105 shadow-[0_0_20px_rgba(203,213,225,0.4)]',
        questionHeader: 'bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 border-b-2 border-slate-400',
        answerFront: 'from-slate-500 to-slate-700 border-slate-400 shadow-[inset_0_2px_10px_rgba(255,255,255,0.2)]',
        answerFrontCircle: 'bg-slate-900 border-slate-400',
        answerBack: 'from-slate-200 to-slate-400 text-slate-900 border-slate-100 shadow-[inset_0_2px_10px_rgba(255,255,255,0.5)]',
        answerBackDivider: 'border-slate-900'
      },
      'cyberpunk': {
        teamInactive: 'border-transparent bg-indigo-950/80 shadow-md backdrop-blur-sm',
        teamActive: 'border-cyan-400 bg-indigo-900 scale-105 shadow-[0_0_25px_rgba(34,211,238,0.6)]',
        questionHeader: 'bg-indigo-950 border-b-2 border-pink-500 shadow-[0_5px_15px_rgba(236,72,153,0.3)]',
        answerFront: 'from-purple-700 to-indigo-900 border-pink-500 shadow-[inset_0_2px_10px_rgba(236,72,153,0.3)]',
        answerFrontCircle: 'bg-indigo-950 border-pink-400 text-pink-400 shadow-[0_0_10px_rgba(236,72,153,0.5)]',
        answerBack: 'from-pink-500 to-fuchsia-600 text-indigo-950 border-cyan-300 shadow-[inset_0_2px_10px_rgba(255,255,255,0.4)]',
        answerBackDivider: 'border-indigo-950'
      }
    };
    return themes[theme] || themes['default'];
  };

  const themeClasses = getThemeClasses();

  if (!currentQuestion) return <div className="p-10 text-center">No questions found. Go back to setup.</div>;

  return (
    <div 
      ref={containerRef}
      className="min-h-[1000px] max-h-screen overflow-y-auto bg-gray-900 text-white p-4 flex flex-col items-center bg-cover bg-center bg-no-repeat"
      style={backgroundImageUrl ? { backgroundImage: `url(${backgroundImageUrl})` } : {}}
    >
      {/* Fullscreen Button */}
      <div className="w-full flex justify-end">
        <button 
          onClick={toggleFullscreen}
          className="p-2 bg-black/40 hover:bg-black/60 backdrop-blur-sm rounded-full transition-all border border-white/20 shadow-lg"
          title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
        >
          {isFullscreen ? <Minimize2 size={24} /> : <Maximize2 size={24} />}
        </button>
      </div>
      {/* Teams Header */}
      <div className="w-full max-w-6xl flex justify-between items-start mb-8">
        {/* Team 1 */}
        <div 
          onClick={() => setActiveTeamIdx(0)}
          className={`p-4 rounded-xl border-4 transition-all cursor-pointer min-w-[16rem] text-center ${
            activeTeamIdx === 0 ? themeClasses.teamActive : themeClasses.teamInactive
          }`}
        >
          <div className="flex flex-wrap justify-center mb-2 gap-2">
            {teams[0]?.players.length > 0 ? (
              teams[0].players.map((player, pIdx) => (
                <div key={pIdx} className="relative group">
                  {player.image ? (
                    <img 
                      src={player.image} 
                      className="w-20 h-20 rounded-full border-2 border-white object-cover" 
                      alt={player.name} 
                      style={{ maxWidth: '100px', maxHeight: '100px' }}
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-full border-2 border-white bg-gray-700 flex items-center justify-center">
                      <User size={32} className="text-gray-400" />
                    </div>
                  )}
                  {player.name && (
                    <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 bg-black bg-opacity-75 text-[10px] px-1 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
                      {player.name}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <User size={48} className="text-gray-400" />
            )}
          </div>
          <h2 className="text-2xl font-bold truncate">{teams[0]?.name}</h2>
          <div className="flex items-center justify-center space-x-4 mt-2">
            <button 
              onClick={(e) => { e.stopPropagation(); updateTeamScore(0, -1); }}
              className="p-1 hover:bg-white/20 rounded-full transition-colors"
            >
              <Minus size={20} className="text-gray-300" />
            </button>
            <input 
              type="number"
              value={teams[0]?.score || 0}
              onChange={(e) => {
                const val = parseInt(e.target.value) || 0;
                const newTeams = [...teams];
                newTeams[0].score = Math.max(0, val);
                setTeams(newTeams);
              }}
              onClick={(e) => e.stopPropagation()}
              className="w-24 text-4xl font-black text-yellow-400 bg-transparent border-none text-center focus:outline-none focus:ring-2 focus:ring-yellow-400 rounded transition-all [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
            <button 
              onClick={(e) => { e.stopPropagation(); updateTeamScore(0, 1); }}
              className="p-1 hover:bg-white/20 rounded-full transition-colors"
            >
              <Plus size={20} className="text-gray-300" />
            </button>
          </div>
        </div>

        {/* Timer */}
        <div className="flex flex-col items-center space-y-4 bg-gray-800 p-6 rounded-2xl border-2 border-gray-700 shadow-inner min-w-[12rem]">
          <div className="flex items-center space-x-2 text-gray-400 uppercase tracking-widest font-bold text-sm">
            <Clock size={16} />
            <span>Timer</span>
          </div>
          <div className={`text-6xl font-mono font-black ${timeLeft <= 5 ? 'text-red-500 animate-pulse' : 'text-white'}`}>
            {timeLeft}
          </div>
          <div className="flex space-x-4">
            <button 
              onClick={toggleTimer}
              className={`p-3 rounded-full transition-all ${isTimerRunning ? 'bg-yellow-600 hover:bg-yellow-700' : 'bg-green-600 hover:bg-green-700'}`}
              title={isTimerRunning ? "Pause" : "Start"}
            >
              {isTimerRunning ? <Pause size={24} /> : <Play size={24} />}
            </button>
            <button 
              onClick={resetTimer}
              className="p-3 bg-gray-600 hover:bg-gray-700 rounded-full transition-all text-white"
              title="Reset Timer"
            >
              <RotateCcw size={24} />
            </button>
          </div>
        </div>

        {/* Team 2 */}
        <div 
          onClick={() => setActiveTeamIdx(1)}
          className={`p-4 rounded-xl border-2 transition-all cursor-pointer min-w-[16rem] text-center ${
            activeTeamIdx === 1 ? themeClasses.teamActive : themeClasses.teamInactive
          }`}
        >
          <div className="flex flex-wrap justify-center mb-2 gap-2">
            {teams[1]?.players.length > 0 ? (
              teams[1].players.map((player, pIdx) => (
                <div key={pIdx} className="relative group">
                  {player.image ? (
                    <img 
                      src={player.image} 
                      className="w-20 h-20 rounded-full border-2 border-white object-cover" 
                      alt={player.name} 
                      style={{ maxWidth: '100px', maxHeight: '100px' }}
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-full border-2 border-white bg-gray-700 flex items-center justify-center">
                      <User size={32} className="text-gray-400" />
                    </div>
                  )}
                  {player.name && (
                    <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 bg-black bg-opacity-75 text-[10px] px-1 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
                      {player.name}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <User size={48} className="text-gray-400" />
            )}
          </div>
          <h2 className="text-2xl font-bold truncate">{teams[1]?.name}</h2>
          <div className="flex items-center justify-center space-x-4 mt-2">
            <button 
              onClick={(e) => { e.stopPropagation(); updateTeamScore(1, -1); }}
              className="p-1 hover:bg-white/20 rounded-full transition-colors"
            >
              <Minus size={20} className="text-gray-300" />
            </button>
            <input 
              type="number"
              value={teams[1]?.score || 0}
              onChange={(e) => {
                const val = parseInt(e.target.value) || 0;
                const newTeams = [...teams];
                newTeams[1].score = Math.max(0, val);
                setTeams(newTeams);
              }}
              onClick={(e) => e.stopPropagation()}
              className="w-24 text-4xl font-black text-yellow-400 bg-transparent border-none text-center focus:outline-none focus:ring-2 focus:ring-yellow-400 rounded transition-all [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
            <button 
              onClick={(e) => { e.stopPropagation(); updateTeamScore(1, 1); }}
              className="p-1 hover:bg-white/20 rounded-full transition-colors"
            >
              <Plus size={20} className="text-gray-300" />
            </button>
          </div>
        </div>
      </div>

      {/* Main Board */}
      <div className="relative w-full max-w-5xl bg-black/40 backdrop-blur-xl p-10 rounded-[2rem] border-[3px] border-white/20 shadow-[0_0_50px_rgba(0,0,0,0.5)]">
        <h1 className={`text-4xl text-center mb-12 font-black uppercase tracking-widest p-6 rounded-xl shadow-[inset_0_4px_10px_rgba(0,0,0,0.5)] ${themeClasses.questionHeader}`}>
          {currentQuestion.text}
        </h1>

        <div className="grid grid-cols-2 gap-4">
          {reorderAnswers(currentQuestion.answers).map((answer) => (
            <div 
              key={answer.originalIdx} 
              className="perspective-1000 h-20 cursor-pointer"
              onClick={() => revealAnswer(answer.originalIdx)}
            >
              <motion.div 
                className="relative w-full h-full transition-all duration-500 transform-style-3d"
                animate={{ rotateX: revealedAnswers.includes(answer.originalIdx) ? 180 : 0 }}
              >
                {/* Front */}
                <div className={`absolute inset-0 backface-hidden bg-gradient-to-b border-2 rounded flex items-center justify-center text-3xl font-bold shadow-lg ${themeClasses.answerFront}`}>
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center border-2 ${themeClasses.answerFrontCircle}`}>
                    {answer.originalIdx + 1}
                  </div>
                </div>
                {/* Back */}
                <div className={`absolute inset-0 backface-hidden bg-gradient-to-b rounded flex items-center justify-between px-6 text-2xl font-black rotate-x-180 border-2 shadow-lg ${themeClasses.answerBack}`}>
                  <span className="uppercase">{answer.text}</span>
                  <span className={`border-l-2 pl-4 ${themeClasses.answerBackDivider}`}>{answer.responses}</span>
                </div>
              </motion.div>
            </div>
          ))}
          {/* Fill empty spots for visual consistency if needed, or just let it be */}
        </div>

        {/* X Overlay */}
        <AnimatePresence>
          {showX && (
            <motion.div 
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1.5, opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 flex items-center justify-center pointer-events-none"
            >
              <XCircle size={300} className="text-red-600 drop-shadow-2xl" strokeWidth={3} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Controls */}
      <div className="mt-12 flex space-x-8">
        <motion.button 
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={triggerX}
          className="bg-gradient-to-br from-red-500 to-red-700 text-white px-10 py-5 rounded-full text-3xl font-black shadow-[0_10px_20px_rgba(220,38,38,0.4)]"
        >
          STRIKE (X)
        </motion.button>
        <motion.button 
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setCurrentQuestionIdx(prev => Math.min(questions.length - 1, prev + 1))}
          className="bg-gradient-to-br from-green-500 to-emerald-700 text-white px-10 py-5 rounded-full text-3xl font-black shadow-[0_10px_20px_rgba(16,185,129,0.4)]"
        >
          NEXT QUESTION
        </motion.button>
      </div>

      <style>{`
        .perspective-1000 { perspective: 1000px; }
        .transform-style-3d { transform-style: preserve-3d; }
        .backface-hidden { backface-visibility: hidden; }
        .rotate-x-180 { transform: rotateX(180deg); }
      `}</style>
    </div>
  );
};

export default GamePage;
